import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, money } from "@/lib/api";
import { StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Wallet, Plus } from "@phosphor-icons/react";

const STATUS_STYLE = {
  pending: "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))]",
  approved: "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))]",
  rejected: "bg-destructive/15 text-destructive",
};

function StatusPill({ status }) {
  return (
    <span data-testid={`req-status-${status}`}
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[status] || ""}`}>
      {status}
    </span>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

export function CashRequestPanel() {
  const [balance, setBalance] = useState(0);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([
        api.get("/billing/balance"),
        api.get("/billing/payment-requests"),
      ]);
      setBalance(b.data.cash_balance_minor || 0);
      setRequests(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const minor = Math.round(parseFloat(amount) * 100);
    if (!minor || isNaN(minor) || minor <= 0) {
      toast.error("Enter a valid positive amount.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/billing/payment-requests", { amount_minor: minor, note: note || null });
      toast.success("Cash request submitted — awaiting admin approval.");
      setOpen(false); setAmount(""); setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="cash-request-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard testid="cash-balance" icon={Wallet} label="Cash Balance"
          value={money(balance)} tone="success" hint="Credited on admin approval" />
        <div className="md:col-span-2 flex items-end">
          <Button data-testid="add-cash-btn" className="rounded-lg" onClick={() => setOpen(true)}>
            <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add Cash
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="cash-requests-table">
        <div className="px-5 py-3.5 border-b border-border">
          <h3 className="font-semibold text-base">Payment History</h3>
          <p className="text-sm text-muted-foreground">Your Add Cash requests and their status.</p>
        </div>
        {loading ? (
          <div className="p-5 text-sm text-muted-foreground">Loading…</div>
        ) : requests.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="cash-requests-empty">
            No payment requests yet. Click <b>Add Cash</b> to submit one.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Processed</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((r) => (
                <TableRow key={r.id} data-testid={`cash-request-row-${r.id}`}>
                  <TableCell className="text-right tabular-nums font-semibold">{money(r.amount_minor)}</TableCell>
                  <TableCell><StatusPill status={r.status} /></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{fmtDate(r.created_at)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{fmtDate(r.processed_at)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-[220px] truncate">{r.note || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent data-testid="add-cash-dialog">
          <DialogHeader>
            <DialogTitle>Add Cash</DialogTitle>
            <DialogDescription>
              Submit a cash top-up request. Your balance updates only after an admin approves it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Amount (USD)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input data-testid="add-cash-amount" type="number" min="0" step="0.01" className="pl-7"
                  value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="50.00" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Textarea data-testid="add-cash-note" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Reference / payment method details" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-cash-request-btn" onClick={submit} disabled={saving} className="rounded-lg">
              {saving ? "Submitting…" : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
